import { getColorScheme } from '@/hooks/useColorScheme'
import useIsMobile from '@/hooks/useIsMobile'
import { HeadingLabel, MetaDataLabel } from '@/styles/fonts'
import { GrandstandIcon } from '@grandstand-web/bally-web-shared/src/components/GrandstandIcon'
import { TealiumServiceContext } from '@grandstand-web/bally-web-shared/src/services/tealium/TealiumService'
import { getFavTeamForTealium } from '@grandstand-web/bally-web-shared/src/services/tealium/tealiumHelpers'
import { UserServiceContext } from '@grandstand-web/bally-web-shared/src/services/user/UserService'
import { cmsRegionTeamToProps, LogoWithDisc } from '@grandstand-web/ui'
import { useRouter } from 'next/router'
import { useContext } from 'react'
import styled from 'styled-components'
import { GrandstandButton } from '../Buttons'

type FavoritesProps = {
  onBack: () => void
}

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  overflow: auto;
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--canvas);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--on-background-alt);
  }
`

const Header = styled(HeadingLabel)`
  color: var(--on-background);
  div {
    float: left;
    top: 3px;
    right: 10px;
    position: relative;
    @media (min-width: 768px) {
      display: none;
    }
  }
  div {
    float: left;
    top: 3px;
    right: 10px;
    position: relative;
    @media (min-width: 768px) {
      display: none;
    }
  }
`

const PartWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
`

const PartDivider = styled.div`
  width: 100%;
  height: 0px;
  border-top: 1px solid var(--divider-canvas);
`

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const PartHeading = styled(HeadingLabel)`
  color: var(--on-background);
`

const MetaLabel = styled(MetaDataLabel)`
  color: var(--on-background-alt);
`

const InfoField = styled.div`
  display: flex;
  flex-direction: start;
  align-items: center;
  gap: 24px;
`

const InfoButton = styled(GrandstandButton)`
  border: none;
`

const Favorites = ({ onBack }: FavoritesProps) => {
  const router = useRouter()
  const isMobile = useIsMobile()
  const userService = useContext(UserServiceContext)
  const regionalTeams = userService.currentUser?.profile?.region?.all_regional_teams
  const colorScheme = getColorScheme()
  const isLightMode = colorScheme.value === 'light'
  const tealium = useContext(TealiumServiceContext)

  const handleBackClick = () => {
    if (isMobile) {
      router.push('/settings')
      onBack()
    }
  }

  const handleUpdateFavorite = async (teamID: string, team_name: string) => {
    const currentUser = userService.currentUser
    if (!currentUser) {
      return
    }
    if (userService.isInFavorites(teamID)) {
      const favoriteTeamIds = await userService.removeFavorite(teamID)
      tealium.removeFavoriteTeam({
        team_name,
        fav_team: getFavTeamForTealium(currentUser, favoriteTeamIds),
      })
    } else {
      const favoriteTeamIds = await userService.addFavorite(teamID)
      tealium.addFavoriteTeam({
        team_name,
        fav_team: getFavTeamForTealium(currentUser, favoriteTeamIds),
      })
    }
  }

  return (
    <MainContent>
      <PartWrapper>
        <Header onClick={handleBackClick} fontSize="4">
          Favorites
        </Header>
      </PartWrapper>
      <PartDivider />

      <PartWrapper>
        <InfoWrapper>
          <PartHeading fontSize="0">Regional Teams</PartHeading>
          <MetaLabel fontSize="sm">Prioritize content from your favorite regional teams.</MetaLabel>
        </InfoWrapper>
      </PartWrapper>
      <PartDivider />

      {regionalTeams &&
        regionalTeams
          .toSorted((a, b) => {
            const aInFavorites = userService.isInFavorites(a.sport_radar_id)
            const bInFavorites = userService.isInFavorites(b.sport_radar_id)
            if (aInFavorites && !bInFavorites) {
              return -1
            } else if (!aInFavorites && bInFavorites) {
              return 1
            } else {
              return 0
            }
          })
          .map((team, index) => [
            <PartWrapper key={index}>
              <LogoWithDisc altBg={false} {...cmsRegionTeamToProps({ team })} subtitle="" />
              <InfoButton
                onClick={() => handleUpdateFavorite(team.sport_radar_id, team.team)}
                buttonStyle="alt"
                buttonSize="xs"
              >
                <GrandstandIcon
                  icon={userService.isInFavorites(team.sport_radar_id) ? 'favorite_active' : 'favorite_inactive'}
                />
              </InfoButton>
            </PartWrapper>,
            <PartDivider key={`divider-${index}`} />,
          ])}
    </MainContent>
  )
}
export default Favorites
